import { useState, useEffect } from 'react';
import {
  Stack,
  TextField,
  Button,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Avatar,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CommentIcon from '@mui/icons-material/Comment';
import { ActionItem, FeedEvent } from '@urbanmix-tech/shared-js';
import { saveFeedEvent, getFeedEvents } from '@/data';
import { useAuth } from '@/shared/context/AuthProvider';
import { useAlert } from '@/contexts/Alert/AlertContext';
import { convertPseudoDateToRealDate } from '@/helpers/common';

export type TaskCommentsSectionProps = {
  actionItem: ActionItem;
};

export function TaskCommentsSection({ actionItem }: TaskCommentsSectionProps) {
  const [comments, setComments] = useState<FeedEvent[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const { userData, user } = useAuth();
  const { showAlert } = useAlert();

  // Fetch comments when component mounts or task changes
  useEffect(() => {
    loadComments();
  }, [actionItem.id]);

  const loadComments = async () => {
    setLoading(true);
    try {
      const allEvents = await getFeedEvents({
        unitId: actionItem.unitId,
        projectId: actionItem.projectId,
        buildingId: actionItem.buildingId,
      });

      // Filter to only task_comment events for this specific task
      const taskComments = allEvents.filter(
        (event) => event.eventType === 'task_comment' && event.eventData?.taskId === actionItem.id
      );

      // Sort by date, most recent first
      taskComments.sort((a, b) => {
        const dateA = convertPseudoDateToRealDate(a.eventDate);
        const dateB = convertPseudoDateToRealDate(b.eventDate);

        if (!dateA || !dateB) return 0;

        return dateB.getTime() - dateA.getTime();
      });

      setComments(taskComments);
    } catch (error) {
      console.error('Error loading comments:', error);
      showAlert('Failed to load comments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      showAlert('Comment cannot be empty', 'warning');
      return;
    }

    if (!userData || !user) {
      showAlert('You must be logged in to comment', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const commentEvent: FeedEvent = {
        id: `task_comment_${actionItem.id}_${Date.now()}`,
        eventType: 'task_comment',
        eventDate: new Date(),
        projectId: actionItem.projectId,
        buildingId: actionItem.buildingId,
        unitId: actionItem.unitId,
        userId: user.uid,
        eventData: {
          taskId: actionItem.id,
          comment: newComment.trim(),
          authorName: userData.fullName || userData.email,
          authorId: user.uid,
          taskTitle: actionItem.title,
        },
      };

      const result = await saveFeedEvent(commentEvent);

      if (result) {
        setNewComment('');
        showAlert('Comment added successfully', 'success');
        await loadComments();
      } else {
        showAlert('Failed to add comment', 'error');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      showAlert('Failed to add comment', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const formatRelativeTime = (date: Date) => {
    // Convert Firestore Timestamp to JavaScript Date
    const eventDate = convertPseudoDateToRealDate(date);

    if (!eventDate || isNaN(eventDate.getTime())) {
      return 'Invalid Date';
    }

    const now = new Date();
    const diffMs = now.getTime() - eventDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return eventDate.toLocaleDateString();
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{
        backgroundColor: 'transparent',
        boxShadow: 'none',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          padding: 0,
          minHeight: 'auto',
          '& .MuiAccordionSummary-content': {
            margin: '12px 0',
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <CommentIcon fontSize="small" sx={{ color: 'zinc.400' }} />
          <Typography variant="subtitle2" sx={{ color: 'zinc.300' }}>
            Comments {comments.length > 0 && `(${comments.length})`}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        <Stack spacing={2}>
          {/* Comments List */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : comments.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: 'zinc.500', textAlign: 'center', py: 2 }}
            >
              No comments yet. Be the first to comment!
            </Typography>
          ) : (
            <Stack spacing={2}>
              {comments.map((comment) => (
                <Box key={comment.id}>
                  <Stack direction="row" spacing={2}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: '0.75rem',
                        bgcolor: 'primary.main',
                      }}
                    >
                      {getInitials(comment.eventData?.authorName || 'U')}
                    </Avatar>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="baseline"
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: 'zinc.100', fontWeight: 500 }}
                        >
                          {comment.eventData?.authorName || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'zinc.500' }}>
                          {formatRelativeTime(comment.eventDate)}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'zinc.300',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {comment.eventData?.comment}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}

          {/* Divider between comments and add form */}
          {comments.length > 0 && <Divider sx={{ borderColor: 'zinc.700' }} />}

          {/* Add Comment Form */}
          <Stack spacing={1}>
            <TextField
              multiline
              rows={3}
              fullWidth
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(39, 39, 42, 0.5)',
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleAddComment}
                disabled={submitting || !newComment.trim()}
              >
                {submitting ? 'Adding...' : 'Add Comment'}
              </Button>
            </Box>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
